import { useMemo } from 'react';
import { CardActionButtonsProps } from '@/shadcn/components/card-overlay';

export interface UseCardActionButtonsOptions {
  /** Show download button */
  showDownload?: boolean;
  /** Show external link button */
  showExternal?: boolean;
  /** Download button click handler */
  onDownload?: () => void;
  /** External link button click handler */
  onOpenExternal?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Custom action buttons to render */
  children?: React.ReactNode;
}

export interface UseCardActionButtonsResult {
  /** Props for CardActionButtons component */
  cardActionButtonsProps: CardActionButtonsProps;
  /** Whether any actions are available */
  hasActions: boolean;
}

/**
 * useCardActionButtons
 * --------------------
 * Hook to build props for CardActionButtons component.
 * Provides common configurations and validation logic.
 *
 * @param options - Configuration options for action buttons
 * @returns Object with cardActionButtonsProps and hasActions flag
 */
export const useCardActionButtons = (
  options: UseCardActionButtonsOptions = {}
): UseCardActionButtonsResult => {
  const {
    showDownload = false,
    showExternal = true,
    onDownload,
    onOpenExternal,
    className,
    children,
  } = options;

  const cardActionButtonsProps = useMemo<CardActionButtonsProps>(() => ({
    showDownload,
    showExternal,
    onDownload,
    onOpenExternal,
    className,
    children,
  }), [showDownload, showExternal, onDownload, onOpenExternal, className, children]);

  const hasActions = useMemo(() => {
    return (
      (showDownload && Boolean(onDownload)) ||
      (showExternal && Boolean(onOpenExternal)) ||
      Boolean(children)
    );
  }, [showDownload, onDownload, showExternal, onOpenExternal, children]);

  return {
    cardActionButtonsProps,
    hasActions,
  };
};

/**
 * useContentPreviewActionButtons
 * ------------------------------
 * Preset hook for content preview cards with download and external actions.
 */
export const useContentPreviewActionButtons = (
  onDownload?: () => void,
  onOpenExternal?: () => void,
  showDownload: boolean = true
): UseCardActionButtonsResult => {
  return useCardActionButtons({
    showDownload,
    showExternal: true,
    onDownload,
    onOpenExternal,
  });
};

/**
 * useSimilarCreatorsActionButtons
 * -------------------------------
 * Preset hook for similar creators cards with external action only.
 */
export const useSimilarCreatorsActionButtons = (
  onOpenExternal?: () => void,
  onDownload?: () => void,
  showDownload: boolean = true
): UseCardActionButtonsResult => {
  return useCardActionButtons({
    showDownload,
    showExternal: true,
    onDownload,
    onOpenExternal,
  });
};
